import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { usePageContext } from '../../hooks/contexts/usePageContext';
import * as styles from './prevNextArticleLinks.module.scss';
import { LocaleLink } from '../../components/2.0version/localeLink/LocaleLink';
import {
  ArticleTemplateQuery,
  PrevNextArticleLinksQuery,
  SanityBlogArticle,
  SanityIncontournablesItem,
} from '../../../graphql-types';
import { URLS } from '../../../constants';

interface IPrevNextArticleLinksProps {
  data?: ArticleTemplateQuery;
}

export const PrevNextArticleLinks = ({ data }: IPrevNextArticleLinksProps) => {
  const { lang } = usePageContext();
  const { allSanityBlogArticle, allSanityIncontournablesItem } =
    useStaticQuery<PrevNextArticleLinksQuery>(QUERY);

  if (!data) return null;

  const isIncontournable = Boolean(data?.sanityIncontournablesItem?.id);

  const currentList = isIncontournable
    ? allSanityIncontournablesItem.nodes
    : allSanityBlogArticle.nodes;

  const currentItem = isIncontournable ? data?.sanityIncontournablesItem : data?.sanityBlogArticle;

  const currentIndex =
    currentList?.findIndex((listElement) => listElement?.id === currentItem?.id) ?? 0;

  const prevElement =
    currentIndex === 0 ? currentList?.[currentList.length - 1] : currentList?.[currentIndex - 1];
  const nextElement =
    currentIndex === (currentList?.length ?? 1) - 1
      ? currentList?.[0]
      : currentList?.[currentIndex + 1];

  let prevCategory = '';
  let nextCategory = '';
  let prevUrlBase = '';
  let nextUrlBase = '';
  let categoryTitle = '';

  if (
    prevElement.__typename === 'SanityBlogArticle' &&
    nextElement.__typename === 'SanityBlogArticle'
  ) {
    prevCategory = prevElement?.category?.[0]?._rawSlug?.current;
    nextCategory = nextElement?.category?.[0]?._rawSlug?.current;
    prevUrlBase = `${URLS.blog.url}/${prevCategory}`;
    nextUrlBase = `${URLS.blog.url}/${nextCategory}`;
    categoryTitle = data?.sanityBlogPage?.pageHeader?._rawTitle?.[lang];
  } else {
    prevUrlBase = URLS.incontournables.url;
    nextUrlBase = URLS.incontournables.url;
    categoryTitle = data?.sanityIncontournablesPage?.pageHeader?._rawTitle?.[lang];
  }

  return (
    <div className={styles.wrapper}>
      <LocaleLink to={`${prevUrlBase}/${prevElement?.slug?.current}`} className={styles.link}>
        <FontAwesomeIcon icon={faChevronLeft} size="sm" />
        {prevElement?.pageHeader?._rawTitle?.[lang]}
      </LocaleLink>
      <div className={styles.categoryTitle}>{categoryTitle}</div>

      <LocaleLink to={`${nextUrlBase}/${nextElement?.slug?.current}`} className={styles.link}>
        {nextElement?.pageHeader?._rawTitle?.[lang]}
        <FontAwesomeIcon icon={faChevronRight} size="sm" />
      </LocaleLink>
    </div>
  );
};

const QUERY = graphql`
  query PrevNextArticleLinks {
    allSanityIncontournablesItem {
      nodes {
        __typename
        id
        slug {
          current
        }
        pageHeader {
          _rawTitle
        }
      }
    }

    allSanityBlogArticle {
      nodes {
        __typename
        id
        category {
          _rawSlug
        }
        slug {
          current
        }
        pageHeader {
          _rawTitle
        }
      }
    }

    sanityIncontournablesPage {
      pageHeader {
        _rawTitle
      }
    }

    sanityBlogPage {
      pageHeader {
        _rawTitle
      }
    }
  }
`;
